import Form from "@rjsf/react-bootstrap";
import React, { FunctionComponent, useEffect } from "react";
import { JSONSchema7 } from "json-schema";
import { Link, useNavigate, useLocation } from "react-router";
import { toast } from "react-toastify";
import firebase from "firebase/compat/app";
import validator from "@rjsf/validator-ajv8";
import { UiSchema } from "@rjsf/utils";
import { Container, Card, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet";

import { firebaseAppAuth } from "../../lib/firebase";
import { PrimaryButton } from "../common/button/Buttons";
import { trackEvent, trackFormEvent } from "../../helper/analytics";
import { trackPlausibleGoal, PlausibleGoals } from "../../helper/plausibleAnalytics";
import WebtieLogo from "../common/webtie-logo.png";
import FirebaseService from "../../actions/firebase/FirebaseActions";
import { useUserContext } from "../../contexts/UserContext";

const schema: JSONSchema7 = {
  type: "object",
  required: ["email", "password"],
  properties: {
    email: {
      type: "string",
      title: "E-Mail"
    },
    password: {
      type: "string",
      title: "Passwort"
    }
  }
};

const uiSchema: UiSchema = {
  email: {
    "ui:widget": "email"
  },
  password: {
    "ui:widget": "password"
  }
};

interface Props {
  user: firebase.User | null | undefined;
}

const SignIn: FunctionComponent<Props> = ({ user }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setActiveUser } = useUserContext();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const searchParams = new URLSearchParams(location.search);
    const nextPage = searchParams.get("nextPage");

    if (nextPage) {
      navigate(decodeURIComponent(nextPage));
    } else {
      FirebaseService.getUserDefaultForm(user.uid)
        .then(async (defaultForm) => {
          if (cancelled) return;
          const formOwnerUser = await FirebaseService.getUser(defaultForm.formOwnerId);
          if (cancelled) return;
          setActiveUser(formOwnerUser);
          navigate(`/user/${formOwnerUser.id}/dashboard`);
        })
        .catch(async () => {
          if (cancelled) return;
          try {
            await FirebaseService.setDefaultFormOwner(user.uid, user.uid);
            if (cancelled) return;
            const ownUser = await FirebaseService.getUser(user.uid);
            if (cancelled) return;
            setActiveUser(ownUser);
            navigate(`/user/${ownUser.id}/dashboard`);
          } catch (error) {
            if (cancelled) return;
            console.error("Error creating default form:", error);
            navigate(`/user/${user.uid}/dashboard`);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [user, navigate, location.search, setActiveUser]);

  const submitSignIn = async (formData: any): Promise<void> => {
    const { email, password } = formData;
    trackFormEvent("started", "login-form");

    try {
      if (firebaseAppAuth) {
        await firebaseAppAuth.signInWithEmailAndPassword(email, password);
        trackEvent("user_login", {
          method: "email_password",
          timestamp: new Date().toISOString()
        });
        trackFormEvent("completed", "login-form");
        trackPlausibleGoal(PlausibleGoals.USER_LOGIN);
      }
    } catch (error) {
      console.warn(error);
      trackEvent("login_error", {
        error: error instanceof Error ? error.message : "Unknown error",
        timestamp: new Date().toISOString()
      });
      trackFormEvent("error", "login-form", {
        error: error instanceof Error ? error.message : "Authentication failed"
      });
      toast.error("Anmeldedaten sind nicht korrekt. Bitte versuchen Sie es erneut.");
    }
  };

  return (
    <>
      <Helmet>
        <title>Spielgruppe Online - Anmelden</title>
        <meta name="description" content="Melden Sie sich an, um Ihre Spielgruppe-Anmeldungen zu verwalten" />
      </Helmet>
      <div
        className="min-vh-100 d-flex flex-column"
        style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <Container className="flex-grow-1 d-flex align-items-center justify-content-center py-5">
          <Row className="w-100 justify-content-center">
            <Col md={6} lg={5} xl={4}>
              <Card className="shadow-lg border-0 rounded-lg">
                <Card.Body className="p-5">
                  {/* Webtie Logo */}
                  <div className="text-center mb-4">
                    <img
                      src={WebtieLogo}
                      alt="Webtie Logo"
                      className="img-fluid mb-3"
                      style={{ maxWidth: "200px", height: "auto" }}
                    />
                    <h2 className="text-dark mb-3" style={{ fontFamily: "Varela, sans-serif", fontWeight: "600" }}>
                      Willkommen zurück
                    </h2>
                    <p className="text-muted mb-4">Melden Sie sich an, um Ihre Anmeldungen zu verwalten</p>
                  </div>

                  <Form
                    validator={validator}
                    schema={schema}
                    uiSchema={uiSchema}
                    onSubmit={(e) => submitSignIn(e.formData)}
                    className="login-form">
                    <div className="d-grid gap-2 mt-4">
                      <PrimaryButton type="submit" size="lg">
                        Anmelden
                      </PrimaryButton>
                    </div>
                  </Form>

                  <div className="text-center mt-4">
                    <p className="text-muted mb-2">
                      Haben Sie noch keinen Account?{" "}
                      <Link to="/signup" className="text-decoration-none fw-bold">
                        Hier gehts zur Registrierung
                      </Link>
                    </p>
                    <p className="text-muted">
                      Passwort vergessen?{" "}
                      <Link to="/reset-password" className="text-decoration-none fw-bold">
                        Hier setzen Sie es zurück
                      </Link>
                    </p>
                  </div>
                </Card.Body>
              </Card>

              <div className="text-center mt-4">
                <p className="text-white-50 small">Spielgruppe Online - Anmeldungen einfach verwalten</p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default SignIn;
